import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useTradesTableData } from '@hub/features/account-dashboard/hooks';
import { getAssetTypeFromAccount } from '@hub/features/account-dashboard/utils';
import { useStore } from '@hub/helpers/use-store';
import { EnvService } from '@hub/services/core/env-service';
import { useMediaQuery, useTheme } from '@mui/material';
import { GridPaginationModel, GridRowParams } from '@mui/x-data-grid';
import { useGetFeatureFlag } from '@monorepo/feature-flag/client';
import type { ClosedPosition, OpenPosition } from '@monorepo/types';
import { AssetClass } from '@monorepo/types';
import { useDashboard } from '../../../../../../contexts';
import {
  ColumnsManagementResetWrapper,
  CustomNoRowsOverlay,
  CustomPagination,
  PositionDetailsModal,
  TradesTableHeader,
  TradesTableStatusBar,
} from './components';
import { TRADES_TABLE_CONSTANTS } from './constants';
import { useTradesTablePersistence } from './hooks';
import { StyledTradesTableContainer } from './styled';
import { createTradesTableColumns, StyledClickableTradesTable } from './table';

export const TradesTableSection: FC = () => {
  const { account } = useDashboard();
  const {
    dataStore: { userStore },
  } = useStore();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.between('xs', 'md'));
  const { t, i18n } = useTranslation('dashboard');

  const enableTradeScore = useGetFeatureFlag('enable-trade-score', false, {
    env: EnvService.application,
  });

  const userId = userStore.currentUser?._id || '';

  const { initialState, onStateChange, resetState } =
    useTradesTablePersistence(userId);

  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: TRADES_TABLE_CONSTANTS.PAGINATION.DEFAULT_PAGE,
    pageSize: TRADES_TABLE_CONSTANTS.PAGINATION.DEFAULT_PAGE_SIZE,
  });

  const accountId = account?.tsAccountId || account?._id || '';

  const {
    selectedViews,
    setSelectedViews,
    positions,
    isLoading,
    totalCount,
    errors,
    refetch,
  } = useTradesTableData({
    accountId,
    account: account || undefined,
    page: paginationModel.page + 1, // Convert 0-based to 1-based
    limit: paginationModel.pageSize,
  });

  useEffect(() => {
    if (errors.closedError || errors.openError) {
      if (paginationModel.page > 0) {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
      }
    }
  }, [errors.closedError, errors.openError, paginationModel.page]);

  useEffect(() => {
    setPaginationModel((prev) => ({
      ...prev,
      page: TRADES_TABLE_CONSTANTS.PAGINATION.DEFAULT_PAGE,
    }));
  }, [selectedViews]);

  const columns = useMemo(
    () =>
      createTradesTableColumns(
        isMobile,
        t,
        account?.tradingSystem,
        enableTradeScore,
        userId,
      ),
    [
      isMobile,
      t,
      account?.tradingSystem,
      enableTradeScore,
      userId,
      i18n.language,
    ],
  );

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedPosition, setSelectedPosition] = useState<
    OpenPosition | ClosedPosition | null
  >(null);

  const assetClass = useMemo(() => getAssetTypeFromAccount(account), [account]);

  const isFutures = assetClass === AssetClass.Futures;

  const handleRowClick = useCallback(
    (params: GridRowParams<OpenPosition | ClosedPosition>) => {
      if (isFutures && params.row) {
        setSelectedPosition(params.row);
        setIsModalOpen(true);
      }
    },
    [isFutures],
  );

  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
    setSelectedPosition(null);
  }, []);

  if (!account) {
    return null;
  }

  return (
    <StyledTradesTableContainer>
      <TradesTableHeader
        selectedViews={selectedViews}
        onSelectionChange={setSelectedViews}
      />
      <StyledClickableTradesTable
        rows={positions}
        columns={columns}
        loading={isLoading}
        keepNonExistentRowsSelected
        disableRowSelectionOnClick
        getRowId={(row) => row.id || row.externalId}
        onRetry={refetch}
        autosizeOptions={TRADES_TABLE_CONSTANTS.AUTOSIZE}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        pageSizeOptions={TRADES_TABLE_CONSTANTS.PAGINATION.PAGE_SIZE_OPTIONS}
        paginationMode="server"
        rowCount={totalCount || 0}
        initialState={initialState}
        onStateChange={onStateChange}
        onRowClick={handleRowClick}
        isClickable={isFutures}
        slots={{
          pagination: CustomPagination,
          noRowsOverlay: CustomNoRowsOverlay,
          columnsManagement: (props) => (
            <ColumnsManagementResetWrapper {...props} onReset={resetState} />
          ),
        }}
      />
      <TradesTableStatusBar
        positions={positions}
        totalCount={totalCount || 0}
      />
      {isFutures && (
        <PositionDetailsModal
          isModalOpen={isModalOpen}
          onClose={handleCloseModal}
          position={selectedPosition}
          tsAccountId={account?.tsAccountId || ''}
        />
      )}
    </StyledTradesTableContainer>
  );
};
